/**
 * BulkImportLeadsModal.js - Bulk Lead Import UI Component
 * 
 * Features:
 * - File upload with drag-and-drop
 * - CSV/Excel preview and validation
 * - Error display and row management
 * - Progress indication during import
 * - Results summary modal
 */

import React, { Component } from "react";
import { CircularProgress, LinearProgress } from "@material-ui/core";
import HelperClass from "../../../config/helperClass";
import { THEME_COLOR, THEME_TEXT_COLOR } from "../../../config/config";
import {
  bulkImportPreview,
  bulkImportExecute,
} from "./api/leads_api";

const auth = new HelperClass();

class BulkImportLeadsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Upload phase
      uploadPhase: "upload", // upload | preview | importing | results
      dragOver: false,
      uploadError: "",
      uploadingFile: false,

      // Preview phase
      fileName: "",
      fileFormat: "",
      previewRows: [],
      previewSummary: { total: 0, valid: 0, invalid: 0, duplicates: 0 },
      excludedRows: new Set(), // Track excluded rows
      selectedAllValid: true,

      // Import phase
      importProgress: 0,
      importError: "",
      
      // Results phase
      importResults: null,

      // File reference for preview re-use
      currentFile: null,
    };
  }

  // ==================== Upload Handlers ====================
  onDragOver = (e) => {
    e.preventDefault();
    this.setState({ dragOver: true });
  };

  onDragLeave = (e) => {
    e.preventDefault();
    this.setState({ dragOver: false });
  };

  onDropFile = async (e) => {
    e.preventDefault();
    this.setState({ dragOver: false });
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      this.handleFileSelect(files[0]);
    }
  };

  onFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      this.handleFileSelect(e.target.files[0]);
    }
  };

  handleFileSelect = async (file) => {
    const validTypes = ["text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      this.setState({ uploadError: "Invalid file type. Please upload CSV or Excel file." });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.setState({ uploadError: "File too large. Maximum size is 5MB." });
      return;
    }

    this.setState({ uploadingFile: true, uploadError: "", currentFile: file });
    
    try {
      const response = await bulkImportPreview(auth.getAccount(), auth.getToken(), file);
      
      if (response && response.data && response.data.status === 200) {
        const { validated_rows, summary, file_format } = response.data.data;
        this.setState({
          uploadPhase: "preview",
          fileName: file.name,
          fileFormat: file_format,
          previewRows: validated_rows || [],
          previewSummary: summary || {},
          uploadingFile: false,
          excludedRows: new Set(),
          selectedAllValid: true,
        });
      } else {
        const msg = (response && response.data && response.data.message) || "Failed to parse file";
        this.setState({ uploadError: msg, uploadingFile: false });
      }
    } catch (e) {
      this.setState({ uploadError: String(e && e.message ? e.message : e), uploadingFile: false });
    }
  };

  // ==================== Preview Handlers ====================
  toggleRowExclusion = (rowIndex) => {
    const { excludedRows } = this.state;
    const newExcluded = new Set(excludedRows);
    if (newExcluded.has(rowIndex)) {
      newExcluded.delete(rowIndex);
    } else {
      newExcluded.add(rowIndex);
    }
    this.setState({ excludedRows: newExcluded });
  };

  toggleSelectAllValid = () => {
    const { previewRows, selectedAllValid } = this.state;
    const validRows = previewRows.filter(r => r.is_valid && !r.is_duplicate);
    const newExcluded = new Set(this.state.excludedRows);

    if (selectedAllValid) {
      // Exclude all valid rows
      validRows.forEach(r => newExcluded.add(r.row_index));
    } else {
      // Include all valid rows
      validRows.forEach(r => newExcluded.delete(r.row_index));
    }

    this.setState({
      excludedRows: newExcluded,
      selectedAllValid: !selectedAllValid,
    });
  };

  downloadSample = () => {
    try {
      const link = document.createElement("a");
      link.href = "/assets/CSV/LeadSample.csv";
      link.setAttribute("download", "LeadSample.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Download error:", e);
    }
  };

  // ==================== Import Handlers ====================
  startImport = async () => {
    const { previewRows, excludedRows } = this.state;

    // Filter out excluded rows and already processed (invalid/duplicates)
    const leadsToImport = previewRows
      .filter(r => !excludedRows.has(r.row_index) && r.is_valid && !r.is_duplicate)
      .map(r => r.lead);

    if (leadsToImport.length === 0) {
      alert("No valid leads to import");
      return;
    }

    this.setState({ uploadPhase: "importing", importProgress: 0, importError: "" });

    try {
      const response = await bulkImportExecute(auth.getAccount(), auth.getToken(), leadsToImport);

      if (response && response.data && response.data.status === 200) {
        this.setState({
          uploadPhase: "results",
          importResults: response.data.data || {},
        });
        
        // Call parent refresh callback
        if (this.props.onImportSuccess) {
          setTimeout(this.props.onImportSuccess, 500);
        }
      } else {
        const msg = (response && response.data && response.data.message) || "Import failed";
        this.setState({ uploadPhase: "preview", importError: msg });
      }
    } catch (e) {
      this.setState({ uploadPhase: "preview", importError: String(e && e.message ? e.message : e) });
    }
  };

  // ==================== Navigation ====================
  reset = () => {
    this.setState({
      uploadPhase: "upload",
      fileName: "",
      fileFormat: "",
      previewRows: [],
      previewSummary: {},
      excludedRows: new Set(),
      uploadError: "",
      importError: "",
      importResults: null,
      currentFile: null,
    });
  };

  closeModal = () => {
    this.reset();
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  // ==================== Render Methods ====================
  renderUploadPhase = () => {
    const { dragOver, uploadingFile, uploadError } = this.state;

    return (
      <>
        <div className="modal-body" style={{ minHeight: 400, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <div
            onDragOver={this.onDragOver}
            onDragLeave={this.onDragLeave}
            onDrop={this.onDropFile}
            style={{
              border: "2px dashed " + (dragOver ? THEME_COLOR : "#d0d0d0"),
              borderRadius: 12,
              padding: 40,
              textAlign: "center",
              cursor: "pointer",
              background: dragOver ? "#f0f7ff" : "#fafafa",
              transition: "all 0.2s",
              width: "100%",
              maxWidth: 500,
            }}
          >
            {uploadingFile ? (
              <>
                <CircularProgress size={48} style={{ marginBottom: 16 }} />
                <p style={{ color: "#666", fontSize: 14 }}>Processing file...</p>
              </>
            ) : (
              <>
                <em className="icon ni ni-cloud-upload" style={{ fontSize: 48, color: THEME_COLOR, display: "block", marginBottom: 12 }}></em>
                <h5 style={{ margin: "16px 0", color: "#1a1a1a" }}>Upload CSV or Excel File</h5>
                <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
                  Drag and drop your file here or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={this.onFileInputChange}
                  style={{ display: "none" }}
                  ref={(input) => (this.fileInput = input)}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: THEME_COLOR, color: THEME_TEXT_COLOR }}
                  onClick={() => this.fileInput.click()}
                >
                  <em className="icon ni ni-folder"></em> Browse Files
                </button>
                <p style={{ color: "#999", fontSize: 12, marginTop: 12 }}>
                  Supported: CSV (Max 5MB)
                </p>
              </>
            )}
          </div>

          <div style={{ marginTop: 24, textAlign: "center" }}>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={this.downloadSample}
              style={{ color: "#3b82f6" }}
            >
              <em className="icon ni ni-download"></em> Download Sample CSV
            </button>
          </div>

          {uploadError && (
            <div style={{ marginTop: 20, width: "100%", maxWidth: 500 }}>
              <div className="alert alert-danger">{uploadError}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={this.closeModal}>
            Close
          </button>
        </div>
      </>
    );
  };

  renderPreviewPhase = () => {
    const { fileName, fileFormat, previewRows, previewSummary, excludedRows, selectedAllValid, importError } = this.state;
    const validRows = previewRows.filter(r => r.is_valid && !r.is_duplicate);
    const selectedCount = validRows.length - Array.from(excludedRows).filter(idx => validRows.some(r => r.row_index === idx)).length;

    return (
      <>
        <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {importError && <div className="alert alert-warning mb-3">{importError}</div>}

          <div style={{ marginBottom: 20 }}>
            <h6 style={{ marginBottom: 12, fontWeight: 700 }}>File: {fileName} ({fileFormat.toUpperCase()})</h6>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ background: "#f0f9ff", padding: "12px 16px", borderRadius: 8, minWidth: 140 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Total Rows</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}>{previewSummary.total}</div>
              </div>
              <div style={{ background: "#f0fdf4", padding: "12px 16px", borderRadius: 8, minWidth: 140 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Valid</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#16a34a" }}>{previewSummary.valid}</div>
              </div>
              <div style={{ background: "#fef2f2", padding: "12px 16px", borderRadius: 8, minWidth: 140 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Invalid</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#dc2626" }}>{previewSummary.invalid}</div>
              </div>
              <div style={{ background: "#fffbeb", padding: "12px 16px", borderRadius: 8, minWidth: 140 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Duplicates</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#d97706" }}>{previewSummary.duplicates}</div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16, borderTop: "1px solid #e5e5e5", paddingTop: 16 }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={selectedAllValid}
                onChange={this.toggleSelectAllValid}
                style={{ marginRight: 8 }}
              />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {selectedAllValid ? "Deselect" : "Select"} all valid rows ({validRows.length})
              </span>
            </label>
          </div>

          {/* Rows Table */}
          <div style={{ overflowX: "auto", marginTop: 16 }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8f8f8", borderBottom: "1px solid #e0e0e0" }}>
                  <th style={{ padding: "8px", textAlign: "center", width: 40 }}></th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Line</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Company</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Contact</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Email</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => {
                  const isValid = row.is_valid && !row.is_duplicate;
                  const isExcluded = excludedRows.has(row.row_index);
                  const bgColor = row.is_duplicate ? "#fff5f5" : !row.is_valid ? "#fff5f5" : isExcluded ? "#f5f5f5" : "#ffffff";

                  return (
                    <tr key={row.row_index} style={{ background: bgColor, borderBottom: "1px solid #efefef" }}>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {isValid && (
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => this.toggleRowExclusion(row.row_index)}
                          />
                        )}
                      </td>
                      <td style={{ padding: "8px" }}>{row.line_number}</td>
                      <td style={{ padding: "8px" }}>{row.lead.company}</td>
                      <td style={{ padding: "8px" }}>{row.lead.contact_name || "—"}</td>
                      <td style={{ padding: "8px" }}>{row.lead.email || "—"}</td>
                      <td style={{ padding: "8px" }}>
                        {row.is_duplicate ? (
                          <span style={{ color: "#d97706", fontWeight: 600 }}>Duplicate</span>
                        ) : !row.is_valid ? (
                          <span style={{ color: "#dc2626" }}>Invalid</span>
                        ) : isExcluded ? (
                          <span style={{ color: "#999" }}>Excluded</span>
                        ) : (
                          <span style={{ color: "#16a34a" }}>Ready</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Error Messages */}
          {previewRows.filter(r => !r.is_valid || r.is_duplicate).length > 0 && (
            <div style={{ marginTop: 20, borderTop: "1px solid #e5e5e5", paddingTop: 16 }}>
              <h6 style={{ marginBottom: 12, fontWeight: 700, color: "#dc2626" }}>Issues</h6>
              {previewRows.map((row) => {
                if ((row.is_valid && !row.is_duplicate) || row.errors.length === 0) return null;

                return (
                  <div key={row.row_index} style={{ marginBottom: 12, padding: 8, background: "#fef2f2", borderRadius: 4, borderLeft: "3px solid #dc2626" }}>
                    <div style={{ fontWeight: 600, color: "#dc2626", marginBottom: 4 }}>Line {row.line_number}:</div>
                    {row.is_duplicate && (
                      <div style={{ color: "#991b1b", fontSize: 12, marginBottom: 4 }}>
                        {row.duplicate_info?.reason}
                      </div>
                    )}
                    {row.errors.map((err, idx) => (
                      <div key={idx} style={{ color: "#991b1b", fontSize: 12 }}>
                        • {err}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={this.reset}>
            Back
          </button>
          <button
            type="button"
            className="btn"
            style={{ background: THEME_COLOR, color: THEME_TEXT_COLOR }}
            disabled={selectedCount === 0}
            onClick={this.startImport}
          >
            <em className="icon ni ni-import" style={{ marginRight: 6 }}></em>
            Import {selectedCount} Leads
          </button>
        </div>
      </>
    );
  };

  renderImportingPhase = () => {
    return (
      <div className="modal-body" style={{ minHeight: 300, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={60} style={{ marginBottom: 24 }} />
        <h5>Importing Leads...</h5>
        <p style={{ color: "#666", marginTop: 12 }}>Please wait while your leads are being processed.</p>
        <LinearProgress style={{ width: 300, marginTop: 24 }} />
      </div>
    );
  };

  renderResultsPhase = () => {
    const { importResults } = this.state;
    if (!importResults) return null;

    const { successful, failed, total } = importResults;
    const successRate = total > 0 ? Math.round((successful / total) * 100) : 0;

    return (
      <div className="modal-body" style={{ minHeight: 300, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>
          {successRate === 100 ? "✅" : successRate >= 50 ? "⚠️" : "❌"}
        </div>

        <h4 style={{ marginBottom: 8 }}>Import Complete</h4>
        <p style={{ color: "#666", marginBottom: 24 }}>
          {successful} out of {total} leads successfully imported
        </p>

        <div style={{ display: "flex", gap: 24, justifyContent: "center", marginBottom: 20 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#16a34a" }}>{successful}</div>
            <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>Successful</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#dc2626" }}>{failed}</div>
            <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>Failed</div>
          </div>
        </div>

        {failed > 0 && (
          <p style={{ color: "#d97706", fontSize: 13, marginBottom: 16 }}>
            Check the failed rows for details and retry if needed
          </p>
        )}
      </div>
    );
  };

  render() {
    const { uploadPhase } = this.state;
    const { isOpen } = this.props;

    if (!isOpen) return null;

    return (
      <div
        className="modal fade show crm-modal"
        style={{ display: "block", background: "rgba(0, 0, 0, 0.5)" }}
        tabIndex="-1"
        onClick={this.closeModal}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-scrollable crm-modal-dialog"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {uploadPhase === "upload" && "Bulk Import Leads"}
                {uploadPhase === "preview" && "Review Import"}
                {uploadPhase === "importing" && "Importing..."}
                {uploadPhase === "results" && "Import Results"}
              </h5>
              {uploadPhase !== "importing" && (
                <button type="button" className="close" onClick={this.closeModal}>
                  <em className="icon ni ni-cross"></em>
                </button>
              )}
            </div>

            {uploadPhase === "upload" && this.renderUploadPhase()}
            {uploadPhase === "preview" && this.renderPreviewPhase()}
            {uploadPhase === "importing" && this.renderImportingPhase()}
            {uploadPhase === "results" && (
              <>
                {this.renderResultsPhase()}
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={this.closeModal}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default BulkImportLeadsModal;